/**
 * BookByPrice Model — central client-side normalization & cache for BBP.
 *
 * Responsibilities (Phase 3):
 * - Subscribe to raw `B` payloads from marketdata (Phase 2 transport).
 * - Normalize raw L levels once per symbol into `bids[]` / `asks[]` arrays.
 * - Cache the latest normalized snapshot per canonical symbol.
 * - Fan out the same snapshot to all model consumers for that symbol.
 * - Lazily register/unregister the raw receiver based on consumer count.
 *
 * Not in this phase: UI rendering, mode-specific shaping, order entry.
 */

import marketdataSingleton from "./marketdata";

const TRANSPORT_PREFIX = "bookByPriceModel:";

const REPLACEMENT_TYPES = new Set(["BP", "UBP", "HBP"]);
const REPEATED_TYPE = "RBP";

function isFiniteNumber(v) {
  return typeof v === "number" && Number.isFinite(v);
}

function toQuantity(v) {
  return isFiniteNumber(v) && v > 0 ? v : 0;
}

function toOrders(v) {
  return isFiniteNumber(v) && v > 0 ? v : 0;
}

export class BookByPriceModel {
  constructor(options = {}) {
    this.marketdata = options.marketdata || marketdataSingleton;
    this.now = options.now || (() => Date.now());
    this.retainRawLevels = options.retainRawLevels === true;
    this.getCanonicalSymbol =
      options.getCanonicalSymbol ||
      ((s) =>
        this.marketdata &&
        typeof this.marketdata.getCanonicalSymbol === "function"
          ? this.marketdata.getCanonicalSymbol(s)
          : s);

    /** @type {Map<string, Map<string, Function>>} canonical → Map<subscriberId, callback> */
    this.subscribersBySymbol = new Map();
    /** @type {Map<string, object>} canonical → latest normalized snapshot */
    this.latestBySymbol = new Map();
    /** @type {Map<string, number>} canonical → monotonic sequence */
    this.sequenceBySymbol = new Map();
    /** @type {Map<string, string>} canonical → last raw T value seen */
    this.lastTypeBySymbol = new Map();
    /** @type {Map<string, number>} canonical → epoch ms of last RBP */
    this.lastRepeatedAtBySymbol = new Map();

    // Diagnostics counters
    this.unknownTypeCount = 0;
    this.rbpWithoutPriorCount = 0;
    this.ignoredNoSubscribersCount = 0;

    // Raw receiver lifecycle
    this._rawReceiver = {
      OnBookByPriceRaw: (book) => this._handleRawBook(book),
    };
    this._rawReceiverRegistered = false;
  }

  // -------------------------------------------------------------------------
  // Validation / normalization helpers
  // -------------------------------------------------------------------------

  _normalizeSymbolString(symbol) {
    if (typeof symbol !== "string") return null;
    const trimmed = symbol.trim().toUpperCase();
    return trimmed.length > 0 ? trimmed : null;
  }

  _resolveCanonical(symbol) {
    const normalized = this._normalizeSymbolString(symbol);
    if (!normalized) return null;
    try {
      return this.getCanonicalSymbol(normalized) || normalized;
    } catch {
      return normalized;
    }
  }

  _validateSubscriberId(subscriberId) {
    return typeof subscriberId === "string" && subscriberId.trim().length > 0;
  }

  _transportSubscriberId(subscriberId) {
    return `${TRANSPORT_PREFIX}${subscriberId}`;
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /**
   * Subscribe a consumer to normalized BBP snapshots for `symbol`.
   * Delivers the latest cached snapshot synchronously on subscribe (if any).
   *
   * @param {string} symbol         requested symbol (any alias)
   * @param {string} subscriberId   non-empty unique consumer id
   * @param {(snapshot: object) => void} callback
   * @returns {() => void} unsubscribe function (idempotent)
   */
  subscribeBookByPrice(symbol, subscriberId, callback) {
    const canonical = this._resolveCanonical(symbol);
    if (!canonical) {
      console.warn("[BookByPriceModel] subscribe: invalid symbol", symbol);
      return () => {};
    }
    if (!this._validateSubscriberId(subscriberId)) {
      console.warn("[BookByPriceModel] subscribe: subscriberId required", {
        symbol: canonical,
      });
      return () => {};
    }
    if (typeof callback !== "function") {
      console.warn(
        "[BookByPriceModel] subscribe: callback must be a function",
        { symbol: canonical },
      );
      return () => {};
    }

    let subs = this.subscribersBySymbol.get(canonical);
    if (!subs) {
      subs = new Map();
      this.subscribersBySymbol.set(canonical, subs);
    }

    const isFirstOverall = this._totalSubscribers() === 0;

    subs.set(subscriberId, callback);

    if (isFirstOverall) {
      this._ensureRawReceiverRegistered();
    }

    // Each model consumer forwards its prefixed subscriber id to the transport
    // so Phase 2 diagnostics see distinct callers. Phase 2 ref-counts these,
    // so only one upstream register("bbp", canonical) is actually sent.
    this._registerTransport(canonical, subscriberId);

    // Deliver cached snapshot immediately if present.
    const cached = this.latestBySymbol.get(canonical);
    if (cached) {
      try {
        callback(cached);
      } catch (err) {
        console.error(
          "[BookByPriceModel] callback threw on initial snapshot",
          err,
        );
      }
    }

    return () => this.unsubscribeBookByPrice(symbol, subscriberId);
  }

  /**
   * Remove a previously registered consumer. Idempotent: unknown subscribers
   * are silently ignored.
   */
  unsubscribeBookByPrice(symbol, subscriberId) {
    const canonical = this._resolveCanonical(symbol);
    if (!canonical) return;
    if (!this._validateSubscriberId(subscriberId)) return;

    const subs = this.subscribersBySymbol.get(canonical);
    if (!subs || !subs.has(subscriberId)) return;

    subs.delete(subscriberId);
    this._unregisterTransport(canonical, subscriberId);

    if (subs.size === 0) {
      this.subscribersBySymbol.delete(canonical);
      this.latestBySymbol.delete(canonical);
      this.sequenceBySymbol.delete(canonical);
      this.lastTypeBySymbol.delete(canonical);
      this.lastRepeatedAtBySymbol.delete(canonical);
    }

    if (this._totalSubscribers() === 0) {
      this._releaseRawReceiver();
    }
  }

  /**
   * Latest cached snapshot for `symbol`, or `undefined` if none.
   */
  getLastBookByPrice(symbol) {
    const canonical = this._resolveCanonical(symbol);
    if (!canonical) return undefined;
    return this.latestBySymbol.get(canonical);
  }

  /**
   * Whether the model has at least one active consumer for `symbol`.
   * Distinct from `marketdata.hasBookByPriceSubscribers` (transport-level).
   */
  hasBookByPriceModelSubscribers(symbol) {
    const canonical = this._resolveCanonical(symbol);
    if (!canonical) return false;
    const subs = this.subscribersBySymbol.get(canonical);
    return subs != null && subs.size > 0;
  }

  debugBookByPriceModelState() {
    const symbols = [];
    this.subscribersBySymbol.forEach((subs, symbol) => {
      symbols.push({
        symbol,
        subscriberCount: subs.size,
        subscribers: Array.from(subs.keys()).join(", "),
        sequence: this.sequenceBySymbol.get(symbol) || 0,
        lastType: this.lastTypeBySymbol.get(symbol) || null,
        lastRepeatedAt: this.lastRepeatedAtBySymbol.get(symbol) || null,
        hasSnapshot: this.latestBySymbol.has(symbol),
      });
    });
    return {
      symbols,
      rawReceiverRegistered: this._rawReceiverRegistered,
      unknownTypeCount: this.unknownTypeCount,
      rbpWithoutPriorCount: this.rbpWithoutPriorCount,
      ignoredNoSubscribersCount: this.ignoredNoSubscribersCount,
    };
  }

  /**
   * Test-only helper: wipe all state and detach from transport.
   * Production code must not depend on this method.
   */
  resetBookByPriceStateForTests() {
    this._releaseRawReceiver();
    this.subscribersBySymbol.clear();
    this.latestBySymbol.clear();
    this.sequenceBySymbol.clear();
    this.lastTypeBySymbol.clear();
    this.lastRepeatedAtBySymbol.clear();
    this.unknownTypeCount = 0;
    this.rbpWithoutPriorCount = 0;
    this.ignoredNoSubscribersCount = 0;
  }

  // -------------------------------------------------------------------------
  // Internal — raw receiver lifecycle
  // -------------------------------------------------------------------------

  _ensureRawReceiverRegistered() {
    if (this._rawReceiverRegistered) return;
    if (
      !this.marketdata ||
      typeof this.marketdata.registerReceiver !== "function"
    )
      return;
    this.marketdata.registerReceiver(this._rawReceiver);
    this._rawReceiverRegistered = true;
  }

  _releaseRawReceiver() {
    if (!this._rawReceiverRegistered) return;
    if (
      this.marketdata &&
      typeof this.marketdata.unregisterReceiver === "function"
    ) {
      this.marketdata.unregisterReceiver(this._rawReceiver);
    }
    this._rawReceiverRegistered = false;
  }

  _registerTransport(canonical, subscriberId) {
    if (
      !this.marketdata ||
      typeof this.marketdata.registerBookByPrice !== "function"
    )
      return;
    this.marketdata.registerBookByPrice(
      canonical,
      this._transportSubscriberId(subscriberId),
    );
  }

  _unregisterTransport(canonical, subscriberId) {
    if (
      !this.marketdata ||
      typeof this.marketdata.unregisterBookByPrice !== "function"
    )
      return;
    this.marketdata.unregisterBookByPrice(
      canonical,
      this._transportSubscriberId(subscriberId),
    );
  }

  _totalSubscribers() {
    let total = 0;
    this.subscribersBySymbol.forEach((subs) => {
      total += subs.size;
    });
    return total;
  }

  // -------------------------------------------------------------------------
  // Internal — raw payload handling
  // -------------------------------------------------------------------------

  _handleRawBook(book) {
    if (!book || typeof book !== "object") return;
    const type = typeof book.T === "string" ? book.T : null;
    const sourceSymbol = typeof book.S === "string" ? book.S : null;

    if (!sourceSymbol) return;

    const canonical = this._resolveCanonical(sourceSymbol);
    if (!canonical) return;

    const subs = this.subscribersBySymbol.get(canonical);
    if (!subs || subs.size === 0) {
      this.ignoredNoSubscribersCount += 1;
      return;
    }

    if (REPLACEMENT_TYPES.has(type)) {
      const snapshot = this._buildReplacementSnapshot(
        book,
        canonical,
        sourceSymbol,
        type,
      );
      this.latestBySymbol.set(canonical, snapshot);
      this.lastTypeBySymbol.set(canonical, type);
      this._fanout(canonical, snapshot);
      return;
    }

    if (type === REPEATED_TYPE) {
      this.lastTypeBySymbol.set(canonical, type);
      this.lastRepeatedAtBySymbol.set(canonical, this.now());
      if (!this.latestBySymbol.has(canonical)) {
        this.rbpWithoutPriorCount += 1;
      }
      // No fanout for RBP by default — previous snapshot is still latest.
      return;
    }

    // Unknown T: ignore deterministically.
    this.unknownTypeCount += 1;
  }

  _buildReplacementSnapshot(book, canonical, sourceSymbol, type) {
    const rawLevels = Array.isArray(book.L) ? book.L : [];
    const bids = [];
    const asks = [];

    for (let i = 0; i < rawLevels.length; i += 1) {
      const lvl = rawLevels[i];
      if (!lvl || typeof lvl !== "object") continue;
      const positionalLevel = i + 1;

      if (isFiniteNumber(lvl.BP) && lvl.BP > 0) {
        bids.push({
          level: positionalLevel,
          price: lvl.BP,
          orders: toOrders(lvl.NB),
          quantity: toQuantity(lvl.QB),
        });
      }
      if (isFiniteNumber(lvl.AP) && lvl.AP > 0) {
        asks.push({
          level: positionalLevel,
          price: lvl.AP,
          orders: toOrders(lvl.NA),
          quantity: toQuantity(lvl.QA),
        });
      }
    }

    const sequence = (this.sequenceBySymbol.get(canonical) || 0) + 1;
    this.sequenceBySymbol.set(canonical, sequence);

    const now = this.now();
    const snapshot = {
      symbol: canonical,
      sourceSymbol,
      type,
      receivedAt: now,
      updatedAt: now,
      sequence,
      isRepeated: false,
      bids,
      asks,
      levelCount: Math.max(bids.length, asks.length),
    };
    if (this.retainRawLevels) {
      snapshot.rawLevels = rawLevels;
    }
    return snapshot;
  }

  _fanout(canonical, snapshot) {
    const subs = this.subscribersBySymbol.get(canonical);
    if (!subs || subs.size === 0) return;
    // Copy entries to protect against subscribe/unsubscribe during iteration
    const callbacks = Array.from(subs.values());
    for (const cb of callbacks) {
      try {
        cb(snapshot);
      } catch (err) {
        console.error(
          `[BookByPriceModel] callback threw for ${canonical}`,
          err,
        );
      }
    }
  }
}

const bookByPriceModel = new BookByPriceModel();
export default bookByPriceModel;

if (typeof window !== "undefined") {
  window.__BOOK_BY_PRICE_MODEL__ = bookByPriceModel;
}
